import {AdminLayout} from "../../layouts/admin-layout";
import {useEffect, useMemo, useRef, useState} from "react";
import {useUserStats} from "../../contexts/UserStatsContext";
import {getContractDomain, setContractDomain} from "../../services/contractService";
import {isAxiosError} from "axios";

export function ProxyPage() {
    const {profile, loading: profileLoading} = useUserStats();
    const contractAddress = profile?.contract_address ?? null;
    const [contractCopied, setContractCopied] = useState<boolean>(false);
    const contractCopyTimeoutRef = useRef<number | null>(null);
    const saveSuccessTimeoutRef = useRef<number | null>(null);

    const [domainLoading, setDomainLoading] = useState<boolean>(true);
    const [currentDomain, setCurrentDomain] = useState<string>("");
    const [domainIsWorking, setDomainIsWorking] = useState<boolean>(false);

    const [domainInput, setDomainInput] = useState<string>("");
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
    const [saving, setSaving] = useState<boolean>(false);

    const loadDomain = async () => {
        setDomainLoading(true);
        try {
            const res = await getContractDomain();
            setCurrentDomain(res.domain ?? "");
            setDomainIsWorking(Boolean(res.is_working));
        } catch {
            setCurrentDomain("");
            setDomainIsWorking(false);
        } finally {
            setDomainLoading(false);
        }
    };

    useEffect(() => {
        loadDomain();
    }, []);

    useEffect(() => {
        return () => {
            if (contractCopyTimeoutRef.current) {
                window.clearTimeout(contractCopyTimeoutRef.current);
            }
            if (saveSuccessTimeoutRef.current) {
                window.clearTimeout(saveSuccessTimeoutRef.current);
            }
        };
    }, []);

    const handleCopyContractAddress = async () => {
        if (!contractAddress) {
            return;
        }

        try {
            await navigator.clipboard.writeText(contractAddress);
            setContractCopied(true);
            if (contractCopyTimeoutRef.current) {
                window.clearTimeout(contractCopyTimeoutRef.current);
            }
            contractCopyTimeoutRef.current = window.setTimeout(() => {
                setContractCopied(false);
            }, 1500);
        } catch {
            // no-op: если clipboard недоступен/запрещён, просто без эффекта
        }
    };

    const contractAddressDisplay = useMemo(() => {
        if (!contractAddress) {
            return profileLoading ? "…" : "—";
        }
        if (contractAddress.length <= 12) {
            return contractAddress;
        }
        return `${contractAddress.slice(0, 6)}...${contractAddress.slice(-4)}`;
    }, [contractAddress, profileLoading]);

    const domainDisplay = useMemo(() => {
        return currentDomain ? currentDomain : "—";
    }, [currentDomain]);

    const statusDotClass = useMemo(() => {
        if (domainLoading) {
            return "bg-amber-500 shadow-[0_0_8px_#f59e0b]";
        }
        if (!currentDomain) {
            return "bg-rose-500 shadow-[0_0_8px_#f43f5e]";
        }
        return domainIsWorking
            ? "bg-emerald-500 shadow-[0_0_8px_#10b981]"
            : "bg-rose-500 shadow-[0_0_8px_#f43f5e]";
    }, [currentDomain, domainIsWorking, domainLoading]);

    const canSave = domainInput.trim().length > 0 && !saving;

    const handleSave = async () => {
        const nextDomain = domainInput.trim();
        if (!nextDomain || saving) {
            return;
        }

        setSaving(true);
        setSaveError(null);
        setSaveSuccess(null);
        try {
            const res = await setContractDomain({domain: nextDomain});
            if (!res.success) {
                setSaveError(res.error || "Failed to update domain");
                return;
            }

            setDomainInput("");
            setSaveSuccess("Saved successfully");
            if (saveSuccessTimeoutRef.current) {
                window.clearTimeout(saveSuccessTimeoutRef.current);
            }
            saveSuccessTimeoutRef.current = window.setTimeout(() => {
                setSaveSuccess(null);
            }, 2500);
            await loadDomain();
        } catch (err) {
            if (isAxiosError(err)) {
                const serverMsg =
                    (err.response?.data as { error?: string; message?: string } | undefined)?.error ??
                    (err.response?.data as { error?: string; message?: string } | undefined)?.message;
                setSaveError(serverMsg || err.message || "Failed to update domain");
                return;
            }
            setSaveError("Failed to update domain");
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminLayout>
            <div className="w-full max-w-[800px] flex flex-col gap-8 animate-[fadeIn_0.5s_ease-out]">
                <div className="flex flex-col gap-2 text-center sm:text-left">
                    <h2 className="text-4xl font-bold tracking-tight text-white drop-shadow-sm">Blockchain Proxy</h2>
                    <p className="text-lg text-slate-400">Manage domains in your smart contract.</p>
                </div>

                <div className="glass-panel rounded-3xl p-1 overflow-hidden">
                    <div className="bg-black/20 rounded-[20px] p-6 sm:p-8 flex flex-col gap-8">
                        <div
                            className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-white/5">
                            <div className="flex flex-col gap-1">
                                <h3 className="text-xl font-semibold text-white">Contract Configuration</h3>
                                <p className="text-sm text-slate-400">Current active routing parameters</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div
                                className="flex flex-col gap-2 p-4 rounded-2xl bg-white/5 hover:bg-white/[0.07] transition-colors border border-white/5 group relative overflow-hidden">
                                <div
                                    className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                <div className="relative flex items-center gap-2 text-slate-400 text-sm font-medium">
                                    <span className="material-symbols-outlined text-lg">description</span>
                                    Smart Contract Address (BSC Mainnet)
                                </div>
                                <div className="relative flex items-center justify-between gap-2 mt-1">
                                    <code
                                        className="text-lg font-mono text-white/90 truncate"
                                        title={contractAddress ?? undefined}
                                    >
                                        {contractAddressDisplay}
                                    </code>
                                    <button
                                        className={`transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                            contractCopied ? "text-emerald-400" : "text-slate-500 hover:text-white"
                                        }`}
                                        title={contractCopied ? "Copied!" : "Copy Address"}
                                        disabled={!contractAddress}
                                        onClick={handleCopyContractAddress}
                                    >
                                        <span className="material-symbols-outlined text-lg">
                                            {contractCopied ? "done" : "content_copy"}
                                        </span>
                                    </button>
                                </div>
                            </div>

                            <div
                                className="flex flex-col gap-2 p-4 rounded-2xl bg-white/5 hover:bg-white/[0.07] transition-colors border border-white/5 group relative overflow-hidden">
                                <div
                                    className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                <div className="relative flex items-center gap-2 text-slate-400 text-sm font-medium">
                                    <span className="material-symbols-outlined text-lg">language</span>
                                    Current Domain
                                </div>
                                <div className="relative flex items-center justify-between gap-2 mt-1">
                                    <div className="relative flex-1 min-w-0">
                                        <span
                                            className={`text-lg font-semibold text-white/90 truncate transition-opacity ${
                                                domainLoading ? "opacity-0" : "opacity-100"
                                            }`}
                                        >
                                            {domainDisplay}
                                        </span>
                                        {domainLoading && (
                                            <span className="absolute inset-0 flex items-center">
                                                <span className="w-5 h-5 border-2 border-white/15 border-t-white/70 rounded-full animate-spin"></span>
                                            </span>
                                        )}
                                    </div>
                                    <span className={`flex h-2 w-2 rounded-full ${statusDotClass}`}></span>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col gap-5 pt-2">
                            <div className="flex flex-col gap-1">
                                <label className="text-sm font-medium text-slate-300 ml-1" htmlFor="domain-input">Update
                                    Domain Destination</label>
                                <div className="relative">
                                    <div
                                        className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <span className="material-symbols-outlined text-slate-500">link</span>
                                    </div>
                                    <input
                                        className="glass-input w-full rounded-xl py-3.5 pl-11 pr-4 text-base placeholder:text-slate-600 focus:ring-0"
                                        id="domain-input"
                                        placeholder="e.g., myproxyserver.com"
                                        type="text"
                                        value={domainInput}
                                        onChange={(e) => {
                                            setDomainInput(e.target.value);
                                            if (saveError) {
                                                setSaveError(null);
                                            }
                                            if (saveSuccess) {
                                                setSaveSuccess(null);
                                            }
                                        }}
                                    />
                                </div>
                                {saveError && (
                                    <p className="text-xs text-rose-400 ml-1 mt-2">{saveError}</p>
                                )}
                                {!saveError && saveSuccess && (
                                    <p className="text-xs text-emerald-400 ml-1 mt-2">{saveSuccess}</p>
                                )}
                                <p className="text-xs text-slate-500 ml-1 mt-1">Your bots will receive the new domain in approximately 10 minutes. If your old domain is still active, the bots will receive the new domain only after a reboot.</p>
                            </div>
                            <div className="flex justify-end pt-2">
                                <button
                                    className="relative overflow-hidden group rounded-xl bg-primary px-8 py-3 font-semibold text-white shadow-[0_0_20px_rgba(19,91,236,0.4)] transition-all hover:shadow-[0_0_30px_rgba(19,91,236,0.6)] hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={!canSave}
                                    onClick={handleSave}
                                >
                                    <div
                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:animate-[shimmer_1.5s_infinite]"></div>
                                    <span className="relative flex items-center gap-2">
<span className="material-symbols-outlined text-[20px]">save</span>
                                        Save Changes
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    )
}
